'use client'

import * as React from 'react'
import { ComponentPlaceholderIcon } from '@radix-ui/react-icons'
import { clsx } from 'clsx'

import { useDeviceDetect } from '../../hooks/use-device-detect'
import { useFloatingBubblesAnimation } from '../../hooks/use-floating-bubbles-animation'
import { BluredBubble } from './blurred-bubble'
import { $mktButtonText, MktButton } from './button'

export const BlurredButton = ({
  onHoverChange,
  onFocusChange,
  disabled,
  children,
  className,
  loading,
  loadingText = 'Loading…',
}: {
  onHoverChange?: (isHovering: boolean) => void
  onFocusChange?: (isFocusing: boolean) => void
  disabled?: boolean
  children?: React.ReactNode
  className?: string
  loading?: boolean
  loadingText?: string
}) => {
  const { isSafari } = useDeviceDetect()
  const { setTarget } = useFloatingBubblesAnimation()
  const [isHovering, setIsHovering] = React.useState(false)
  const [isFocusing, setIsFocusing] = React.useState(false)

  const isHoveringOrFocusing = isHovering || isFocusing

  // this will be reused, so we store in variable
  const containedChildren = (
    <span
      className={clsx(
        'inline-flex w-full items-center justify-center space-x-1.5',
        loading && 'animate-fade-out'
      )}
    >
      {children}
    </span>
  )

  return (
    <div
      className={clsx(className, 'relative w-fit')}
      onPointerEnter={() => {
        onHoverChange?.(true)
        setIsHovering(true)
      }}
      onPointerLeave={() => {
        onHoverChange?.(false)
        setIsHovering(false)
      }}
      onPointerMove={() => {
        onHoverChange?.(true)
        setIsHovering(true)
      }}
    >
      <div
        className="pointer-events-none absolute top-0 left-0 h-full w-full transition-opacity duration-300"
        style={{
          opacity: !disabled && isHoveringOrFocusing ? 1 : 0,
          // in safari this switch is working bad... so weird, i know...
          display: isSafari ? 'none' : undefined,
        }}
      >
        <BluredBubble size={25} className="absolute left-[39px] top-[11px]" />
        <BluredBubble
          size={{ width: 42, height: 12 }}
          className="absolute left-[70px] top-[21px]"
        />
        <BluredBubble size={18} className="absolute left-[119px] top-[17px]" />
      </div>
      <div className="relative overflow-hidden rounded-lg">
        <MktButton
          className="w-full duration-300 focus:!border-dark-border"
          disabled={disabled}
          onFocus={() => {
            onFocusChange?.(true)
            setIsFocusing(true)
          }}
          onBlur={() => {
            onFocusChange?.(false)
            setIsFocusing(false)
          }}
        >
          {containedChildren}
        </MktButton>
        <div
          className="pointer-events-none absolute top-0 left-0 h-full w-full overflow-hidden rounded-lg opacity-70 transition-opacity duration-300"
          ref={(el) => {
            setTarget(el?.children ?? null)
          }}
          style={{
            opacity: !disabled && isHoveringOrFocusing ? 1 : 0,
            WebkitMaskImage: '-webkit-radial-gradient(white, black)',
          }}
        >
          <BluredBubble size={48} className="absolute top-3" />
          <BluredBubble size={43} className="absolute top-5 right-0" />
          <BluredBubble size={33} className="absolute top-7 left-20" />
        </div>
        <div
          className={$mktButtonText({
            className: clsx(
              'pointer-events-none absolute top-1/2 left-1/2 z-10 inline-flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center transition duration-300',
              disabled && 'text-dark-shade-hover'
            ),
          })}
        >
          {containedChildren}
          {loading && (
            <span
              className="invisible absolute !ml-0 inline-flex animate-fade-in items-center justify-center space-x-1.5 opacity-0"
              style={{ animationDelay: '0.1s' }}
            >
              {loadingText && <span>{loadingText}</span>}
              <ComponentPlaceholderIcon className="animate-loading" />
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
